import { useCallback, useMemo } from 'react'
import { useLocation, useParams } from 'wouter'
import { skipToken, useSuspenseQuery } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import { PromotionForm } from '@/features/promotion'
import { DialogHeader } from '@/layouts/Dialog'
import { SelectGoodie } from '@/features/goodies'
import { Suspense } from '@/components/ui/Suspense'
import { useCloseModal, useHistoryFrom } from '@/Router'
import { Permission, useCheckPermission } from '@/features/auth'

function CreatePromotion({ goodieToken }: { goodieToken?: string }) {
  const { t } = useLingui()
  const readOnly = !useCheckPermission([Permission.PROMOTION_CREATE])
  const from = useHistoryFrom()

  const [, navigate] = useLocation()
  const closeModal = useCloseModal()

  // When the last open page was the goodie, we return to it when the promotion is closed
  const backOnClose = useMemo(() => {
    if (!from) return false
    return /^\/app\/goodie\/(.+)$/.test(from)
  }, [from])

  const { data } = useSuspenseQuery(
    AdminAPI.goodie.GET_BY_TOKEN,
    goodieToken
      ? {
          variables: { token: goodieToken }
        }
      : skipToken
  )

  const goodie = useMemo(
    () => getFragment(SharedAPI.goodie.GOODIE_FRAGMENT, data?.getGoodieByToken),
    [data]
  )

  const onClose = useCallback(() => {
    if (backOnClose) window.history.back()
    else closeModal()
  }, [backOnClose, closeModal])

  const onSelectGoodie = useCallback(
    (goodie: GraphQL.GoodieFragmentFragment) => {
      navigate(`/create-promotion/${goodie.token}`)
    },
    [navigate]
  )

  if (!goodie) {
    return (
      <div className="flex flex-col gap-2 p-5">
        <DialogHeader
          title={t`Choose Goodie`}
          description={t`You need to choose a Goodie to create a promotion from.`}
        />
        <SelectGoodie onSelectedGoodie={onSelectGoodie} />
      </div>
    )
  }

  const goodieTitle = goodie.title

  return (
    <div className="flex flex-col gap-2 p-5">
      <DialogHeader
        title={t`Create Promotion`}
        description={t`Create a new promotion based on the "${goodieTitle}" Goodie`}
        close={onClose}
      />
      <PromotionForm
        goodieId={goodie.id}
        onDone={onClose}
        readOnly={readOnly}
      />
    </div>
  )
}

export default function CreatePromotionSuspense() {
  const params = useParams() as { goodieToken?: string }

  if (!params) return

  return (
    <Suspense isDialog>
      <CreatePromotion goodieToken={params.goodieToken} />
    </Suspense>
  )
}
